import { FEEDBACK_URL } from '@aloft/toolkit-shared';
import { LoadingIndicator, Typography } from '@aloft/toolkit-sui';
import { useQuery } from '@tanstack/react-query';

import { useState } from 'react';

import { useCalculatePricingBreakdown } from '../../hooks/useCalculatePricingBreakdown.ts';
import type { CheckoutProps, PricingBreakdown } from './types.ts';

type Props = {
  priceID: CheckoutProps['priceID'];
  isTrial: boolean;
  children: (props: { pricingBreakdown: PricingBreakdown }) => React.ReactNode;
};

/** Fetches initial pricing breakdown data for `<Checkout>`. */
export const CheckoutFetcher = (props: Props) => {
  const { priceID, isTrial, children } = props;
  const [pricingBreakdown, setPricingBreakdown] = useState<PricingBreakdown | null>(null);

  const { calculatePricingBreakdown } = useCalculatePricingBreakdown();

  const getInitialPricingBreakdown = useQuery({
    staleTime: Infinity,
    queryKey: ['initialPricingBreakdown', priceID],
    queryFn: async () => {
      const res = await calculatePricingBreakdown({
        isTrial: !!isTrial,
        priceID,
        promotionCode: undefined,
      });

      setPricingBreakdown(res);
      return res;
    },
  });

  if (getInitialPricingBreakdown.isError) {
    return (
      <Typography>
        We&apos;re sorry, we couldn&apos;t complete your order. Please reload the page and try
        again, or&nbsp;
        <a
          href={FEEDBACK_URL}
          target="_blank"
          style={{ textDecoration: 'underline', color: 'blue' }}
          rel="noreferrer"
        >
          contact us
        </a>
        &nbsp;if this issue continues.
      </Typography>
    );
  }

  if (!pricingBreakdown || getInitialPricingBreakdown.isLoading) {
    return <LoadingIndicator message="Loading pricing details..." />;
  }

  return children({ pricingBreakdown });
};
